import {
  BillingControllerApiFactory,
  DiscountSettingsControllerApiFactory,
  RecurringPaymentControllerApiFactory,
} from '@ce-openapi/business-subscription-service-ts-axios-client';
import { axios } from '@pukka/auth';
import { constants } from '@pukka/common';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';

import { BusinessSubscriptionModel } from '../../models';

const { MOKA_GOAUTH_SERVICE } = constants;

const BUSINESS_SUBSCRIPTION_API = `${MOKA_GOAUTH_SERVICE}/business-subscription`;
const DISCOUNT_SETTINGS_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/businesses/discounts/settings`;
const BILLINGS_CALCULATIONS_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/billings/calculations`;
const VERIFY_BILLINGS_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/billings/verify`;
const SUBSCRIBE_RECURRING_PAYMENT_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/recurring-payments/subscriptions`;
const ACTIVE_RECURRING_PAYMENT_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/recurring-payments/active`;
const OUTLET_BILLING_CALCULATION_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/billings/outlet/calculations`;
const VERIFY_OUTLET_BILLING_URL = `${BUSINESS_SUBSCRIPTION_API}/v1/billings/outlet/verify`;

const discountSettingsApi = DiscountSettingsControllerApiFactory(
  undefined,
  BUSINESS_SUBSCRIPTION_API,
  axios as Parameters<typeof DiscountSettingsControllerApiFactory>[2],
);

const billingApi = BillingControllerApiFactory(
  undefined,
  BUSINESS_SUBSCRIPTION_API,
  axios as Parameters<typeof BillingControllerApiFactory>[2],
);

const recurringPaymentApi = RecurringPaymentControllerApiFactory(
  undefined,
  BUSINESS_SUBSCRIPTION_API,
  axios as Parameters<typeof RecurringPaymentControllerApiFactory>[2],
);

const getDiscountSettings = async (businessId: number) =>
  discountSettingsApi.getDiscountSettingsByBusinessId(businessId).then(({ data }) => data);

const getBillingsCalculations = async (businessId: number) => billingApi.getBillingCalculations(businessId).then(({ data }) => data);

const verifyBilling = async (params: BusinessSubscriptionModel.VerifyBillingRequestParameter) =>
  billingApi
    .verify(params)
    .then(({ data }) => data)
    .catch((error) => {
      return Promise.reject(error.response);
    });

const subscribeRecurringPayment = async (params: BusinessSubscriptionModel.CreateRecurringPaymentRequestParameter) =>
  recurringPaymentApi
    .subscribe(params)
    .then(({ data }) => data)
    .catch((error) => {
      return Promise.reject(error.response);
    });

const getActiveRecurringPayment = async () => recurringPaymentApi.getRecurringPayment().then(({ data }) => data);

const getOutletBillingCalculation = async (outletId: number) => billingApi.getOutletBillingCalculation(outletId).then(({ data }) => data);

const verifyOutletBilling = async (outletId: number) => billingApi.verifyOutletBilling({ outlet_id: outletId }).then(({ data }) => data);

function useBillingsCalculations(
  businessId?: number,
  params?: UseQueryOptions<unknown, unknown, BusinessSubscriptionModel.BillingCalculation[]>,
) {
  return useQuery({
    queryKey: ['billings-calculation'],
    queryFn: () => getBillingsCalculations(businessId as number),
    enabled: businessId !== undefined,
    ...params,
  });
}

function useActiveRecurringPayment() {
  return useQuery({
    queryKey: ['billing-active-recurring'],
    queryFn: () => getActiveRecurringPayment(),
  });
}

export {
  DISCOUNT_SETTINGS_URL,
  BILLINGS_CALCULATIONS_URL,
  VERIFY_BILLINGS_URL,
  SUBSCRIBE_RECURRING_PAYMENT_URL,
  ACTIVE_RECURRING_PAYMENT_URL,
  OUTLET_BILLING_CALCULATION_URL,
  VERIFY_OUTLET_BILLING_URL,
  getDiscountSettings,
  getBillingsCalculations,
  verifyBilling,
  subscribeRecurringPayment,
  getActiveRecurringPayment,
  getOutletBillingCalculation,
  verifyOutletBilling,
  useBillingsCalculations,
  useActiveRecurringPayment,
};
